import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Mail,
  MessageSquare,
  Settings,
  Send,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Server,
  Globe,
  Edit3,
  Save,
  X,
  RotateCcw,
  Database,
  Cloud,
  FileText,
  Filter,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { API_URL } from "@/config/api";

export default function AdminConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  
  // Logs state
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState("all");
  
  // Edit modes
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingWhatsApp, setEditingWhatsApp] = useState(false);
  const [editingGeneral, setEditingGeneral] = useState(false);
  
  // Edit form states
  const [emailForm, setEmailForm] = useState({});
  const [whatsappForm, setWhatsappForm] = useState({});
  const [generalForm, setGeneralForm] = useState({});
  
  // Test states
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("booking@y-go.fr");
  const [testWhatsAppNumber, setTestWhatsAppNumber] = useState("+33278780404");
  
  // Save states
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [resetting, setResetting] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchConfig();
  }, [token, navigate]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [configRes, diagnosticRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/config`, { headers }),
        axios.get(`${API_URL}/api/admin/config/diagnostic`, { headers })
      ]);
      setConfig(configRes.data);
      setDiagnostic(diagnosticRes.data);
      
      // Initialize form states
      setEmailForm({
        mailgun_api_key: "",
        mailgun_domain: configRes.data.email?.mailgun_domain || "",
        email_method: configRes.data.email?.email_method || "api",
      });
      setWhatsappForm({
        sandbox_mode: configRes.data.whatsapp?.sandbox_mode ?? true,
        twilio_account_sid: "",
        twilio_auth_token: "",
        twilio_whatsapp_from: configRes.data.whatsapp?.production_number || "",
        admin_whatsapp: configRes.data.whatsapp?.admin_whatsapp || "",
      });
      setGeneralForm({
        frontend_url: configRes.data.general?.frontend_url || "",
        admin_email: configRes.data.email?.admin_email || "",
      });
    } catch (error) {
      console.error("Failed to fetch config:", error);
      if (error.response?.status === 401) {
        toast.error("Session expirée. Reconnectez-vous.");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Échec du chargement de la configuration");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (filter = "all") => {
    setLogsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API_URL}/api/admin/config/logs/filter?filter_type=${filter}&limit=300`,
        { headers }
      );
      setLogs(response.data);
      setLogsFilter(filter);
    } catch (error) {
      toast.error("Erreur lors du chargement des logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Veuillez entrer une adresse email");
      return;
    }
    
    setTestingEmail(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/config/test-email`,
        { recipient_email: testEmailAddress },
        { headers }
      );
      
      if (response.data.success) {
        toast.success(`Email de test envoyé à ${testEmailAddress}`);
      } else {
        toast.error(`Échec: ${response.data.error || response.data.message}`);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du test");
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testWhatsAppNumber) {
      toast.error("Veuillez entrer un numéro de téléphone");
      return;
    }
    
    setTestingWhatsApp(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/config/test-whatsapp`,
        { recipient_phone: testWhatsAppNumber },
        { headers }
      );
      
      if (response.data.success) {
        toast.success(`WhatsApp envoyé depuis ${response.data.details?.from_number}`);
      } else {
        toast.error(`Échec: ${response.data.error || response.data.message}`);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du test");
    } finally {
      setTestingWhatsApp(false);
    }
  };

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {};
      
      if (emailForm.mailgun_api_key) payload.mailgun_api_key = emailForm.mailgun_api_key;
      if (emailForm.mailgun_domain) payload.mailgun_domain = emailForm.mailgun_domain;
      if (emailForm.email_method) payload.email_method = emailForm.email_method;
      
      await axios.put(`${API_URL}/api/admin/config/email`, payload, { headers });
      toast.success("Configuration email sauvegardée");
      setEditingEmail(false);
      fetchConfig();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    setSavingWhatsApp(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        sandbox_mode: whatsappForm.sandbox_mode,
      };
      
      if (whatsappForm.twilio_account_sid) payload.twilio_account_sid = whatsappForm.twilio_account_sid;
      if (whatsappForm.twilio_auth_token) payload.twilio_auth_token = whatsappForm.twilio_auth_token;
      if (whatsappForm.twilio_whatsapp_from) payload.twilio_whatsapp_from = whatsappForm.twilio_whatsapp_from;
      if (whatsappForm.admin_whatsapp) payload.admin_whatsapp = whatsappForm.admin_whatsapp;
      
      await axios.put(`${API_URL}/api/admin/config/whatsapp`, payload, { headers });
      toast.success("Configuration WhatsApp sauvegardée");
      setEditingWhatsApp(false);
      fetchConfig();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {};
      
      if (generalForm.frontend_url) payload.frontend_url = generalForm.frontend_url;
      if (generalForm.admin_email) payload.admin_email = generalForm.admin_email;
      
      await axios.put(`${API_URL}/api/admin/config/general`, payload, { headers });
      toast.success("Configuration générale sauvegardée");
      setEditingGeneral(false);
      fetchConfig();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleResetConfig = async () => {
    if (!confirm("Êtes-vous sûr de vouloir réinitialiser toute la configuration ? Cela supprimera tous les paramètres personnalisés.")) {
      return;
    }
    
    setResetting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/config/reset`, { headers });
      toast.success("Configuration réinitialisée");
      fetchConfig();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const StatusBadge = ({ status }) => {
    if (status === "ok") return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" /> OK</Badge>;
    if (status === "warning") return <Badge className="bg-amber-100 text-amber-800"><AlertCircle className="w-3 h-3 mr-1" /> Attention</Badge>;
    if (status === "error") return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Erreur</Badge>;
    return <Badge className="bg-slate-100 text-slate-800"><AlertCircle className="w-3 h-3 mr-1" /> Non configuré</Badge>;
  };

  const SourceBadge = ({ fromDb }) => {
    if (fromDb) {
      return <Badge variant="outline" className="text-xs border-purple-300 text-purple-600"><Database className="w-3 h-3 mr-1" /> DB</Badge>;
    }
    return <Badge variant="outline" className="text-xs border-slate-300 text-slate-500"><Cloud className="w-3 h-3 mr-1" /> ENV</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-white hover:bg-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-orange-400" />
                <h1 className="text-xl font-bold">Configuration Serveur</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetConfig}
                disabled={resetting}
                className="border-red-600 text-red-400 hover:bg-red-900/20"
              >
                {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchConfig}
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Environment Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                <CardTitle>Environnement</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingGeneral(!editingGeneral);
                  if (!editingGeneral) {
                    setGeneralForm({
                      frontend_url: config?.general?.frontend_url || "",
                      admin_email: config?.email?.admin_email || "",
                    });
                  }
                }}
              >
                {editingGeneral ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {editingGeneral ? (
              <div className="space-y-4">
                {/* Environment Selector */}
                <div className="p-4 border rounded-lg bg-blue-50">
                  <label className="text-sm font-medium text-blue-800 block mb-2">Environnement</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="environment"
                        checked={generalForm.frontend_url === "https://shore.y-go.fr"}
                        onChange={() => setGeneralForm({...generalForm, frontend_url: "https://shore.y-go.fr"})}
                        className="w-4 h-4 text-green-600"
                      />
                      <span className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">Production</Badge>
                        <code className="text-xs bg-white px-2 py-1 rounded">shore.y-go.fr</code>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="environment"
                        checked={generalForm.frontend_url?.includes("preview.emergentagent.com")}
                        onChange={() => setGeneralForm({...generalForm, frontend_url: "https://seamen-shore-1.preview.emergentagent.com"})}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">Preview</Badge>
                        <code className="text-xs bg-white px-2 py-1 rounded">preview.emergentagent.com</code>
                      </span>
                    </label>
                  </div>
                </div>
                
                {/* URL Display */}
                <div>
                  <label className="text-sm font-medium">URL Frontend (générée automatiquement)</label>
                  <Input
                    value={generalForm.frontend_url}
                    onChange={(e) => setGeneralForm({...generalForm, frontend_url: e.target.value})}
                    placeholder="https://shore.y-go.fr"
                    className="font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">URL utilisée pour générer les liens de tracking. Vous pouvez aussi entrer une URL personnalisée.</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Email Admin</label>
                  <Input
                    value={generalForm.admin_email}
                    onChange={(e) => setGeneralForm({...generalForm, admin_email: e.target.value})}
                    placeholder="booking@y-go.fr"
                  />
                </div>
                <Button onClick={handleSaveGeneral} disabled={savingGeneral} className="bg-green-600 hover:bg-green-700">
                  {savingGeneral ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-slate-500">Environnement</p>
                      <SourceBadge fromDb={config?.general?.from_database?.frontend_url} />
                    </div>
                    <Badge className={
                      config?.general?.environment === "production" 
                        ? "bg-green-100 text-green-800" 
                        : config?.general?.environment === "preview"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                    }>
                      {config?.general?.environment || "Unknown"}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">Détecté via la base de données</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">URL Frontend (active)</p>
                    <p className={`font-mono text-sm truncate font-bold ${config?.general?.url_mismatch ? 'text-red-600' : 'text-green-600'}`}>
                      {config?.general?.frontend_url}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Base de données</p>
                    <p className="font-mono text-sm">{config?.general?.db_name}</p>
                  </div>
                </div>
                
                {/* Warning if URL doesn't match environment */}
                {config?.general?.url_mismatch && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">
                      ⚠️ L'URL active ne correspond pas à l'environnement détecté !
                    </p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="text-red-600">
                        • Environnement : <strong>{config?.general?.environment}</strong>
                      </p>
                      <p className="text-red-600">
                        • URL attendue : <code className="bg-red-100 px-1 rounded">{config?.general?.frontend_url_expected}</code>
                      </p>
                      <p className="text-red-600">
                        • URL actuelle : <code className="bg-red-100 px-1 rounded">{config?.general?.frontend_url}</code>
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                      onClick={async () => {
                        const headers = { Authorization: `Bearer ${token}` };
                        try {
                          await axios.put(
                            `${API_URL}/api/admin/config/general`,
                            { frontend_url: config?.general?.frontend_url_expected },
                            { headers }
                          );
                          toast.success("URL corrigée automatiquement");
                          fetchConfig();
                        } catch (e) {
                          toast.error("Erreur lors de la correction");
                        }
                      }}
                    >
                      Corriger automatiquement → {config?.general?.frontend_url_expected?.replace('https://', '')}
                    </Button>
                  </div>
                )}
                
                {/* Success when URL matches */}
                {!config?.general?.url_mismatch && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      ✅ L'URL est correctement configurée pour l'environnement <strong>{config?.general?.environment}</strong>.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-500" />
                <div>
                  <CardTitle>Configuration Email (Mailgun)</CardTitle>
                  <CardDescription>Envoi des emails de confirmation, notifications et reçus</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={diagnostic?.email?.status} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingEmail(!editingEmail);
                    if (!editingEmail) {
                      setEmailForm({
                        mailgun_api_key: "",
                        mailgun_domain: config?.email?.mailgun_domain || "",
                        email_method: config?.email?.email_method || "api",
                      });
                    }
                  }}
                >
                  {editingEmail ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {editingEmail ? (
              <div className="space-y-4 p-4 border rounded-lg bg-amber-50">
                <h4 className="font-medium text-amber-800">✏️ Mode Édition</h4>
                <div>
                  <label className="text-sm font-medium">Clé API Mailgun</label>
                  <Input
                    type="password"
                    value={emailForm.mailgun_api_key}
                    onChange={(e) => setEmailForm({...emailForm, mailgun_api_key: e.target.value})}
                    placeholder="Laisser vide pour garder la valeur actuelle"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Domaine Mailgun</label>
                  <Input
                    value={emailForm.mailgun_domain}
                    onChange={(e) => setEmailForm({...emailForm, mailgun_domain: e.target.value})}
                    placeholder="mg.y-go.fr"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Méthode d'envoi</label>
                  <select
                    value={emailForm.email_method}
                    onChange={(e) => setEmailForm({...emailForm, email_method: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="api">API (recommandé)</option>
                    <option value="smtp">SMTP</option>
                  </select>
                </div>
                <Button onClick={handleSaveEmail} disabled={savingEmail} className="bg-green-600 hover:bg-green-700">
                  {savingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Clé API Mailgun</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-sm ${config?.email?.mailgun_api_key_set ? "text-green-600" : "text-red-600"}`}>
                        {config?.email?.mailgun_api_key_set ? config?.email?.mailgun_api_key : "❌ Non configurée"}
                      </span>
                      <SourceBadge fromDb={config?.email?.from_database?.mailgun_api_key} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Domaine</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{config?.email?.mailgun_domain}</span>
                      <SourceBadge fromDb={config?.email?.from_database?.mailgun_domain} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Méthode</span>
                    <Badge variant="outline">{config?.email?.email_method?.toUpperCase()}</Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Serveur SMTP</span>
                    <span className="font-mono text-sm">{config?.email?.smtp_host}:{config?.email?.smtp_port}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Utilisateur SMTP</span>
                    <span className="font-mono text-sm">{config?.email?.smtp_user}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Email Admin</span>
                    <span className="font-mono text-sm">{config?.email?.admin_email}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Test Email */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">🧪 Envoyer un email de test</h4>
              <div className="flex gap-3">
                <Input
                  type="email"
                  placeholder="Email destinataire"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  className="max-w-xs"
                />
                <Button
                  onClick={handleTestEmail}
                  disabled={testingEmail}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {testingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer Test
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-500" />
                <div>
                  <CardTitle>Configuration WhatsApp (Twilio)</CardTitle>
                  <CardDescription>Notifications WhatsApp pour admin et chauffeurs</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={diagnostic?.whatsapp?.status} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingWhatsApp(!editingWhatsApp);
                    if (!editingWhatsApp) {
                      setWhatsappForm({
                        sandbox_mode: config?.whatsapp?.sandbox_mode ?? true,
                        twilio_account_sid: "",
                        twilio_auth_token: "",
                        twilio_whatsapp_from: config?.whatsapp?.production_number || "",
                        admin_whatsapp: config?.whatsapp?.admin_whatsapp || "",
                      });
                    }
                  }}
                >
                  {editingWhatsApp ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {editingWhatsApp ? (
              <div className="space-y-4 p-4 border rounded-lg bg-green-50">
                <h4 className="font-medium text-green-800">✏️ Mode Édition</h4>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <h4 className="font-medium">Mode WhatsApp</h4>
                    <p className="text-sm text-slate-500">
                      {whatsappForm.sandbox_mode ? "SANDBOX - Numéro de test" : "PRODUCTION - Votre numéro"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${whatsappForm.sandbox_mode ? "text-amber-600" : "text-slate-400"}`}>
                      Sandbox
                    </span>
                    <Switch
                      checked={!whatsappForm.sandbox_mode}
                      onCheckedChange={(checked) => setWhatsappForm({...whatsappForm, sandbox_mode: !checked})}
                    />
                    <span className={`text-sm font-medium ${!whatsappForm.sandbox_mode ? "text-green-600" : "text-slate-400"}`}>
                      Production
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Account SID Twilio</label>
                  <Input
                    type="password"
                    value={whatsappForm.twilio_account_sid}
                    onChange={(e) => setWhatsappForm({...whatsappForm, twilio_account_sid: e.target.value})}
                    placeholder="Laisser vide pour garder la valeur actuelle"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Auth Token Twilio</label>
                  <Input
                    type="password"
                    value={whatsappForm.twilio_auth_token}
                    onChange={(e) => setWhatsappForm({...whatsappForm, twilio_auth_token: e.target.value})}
                    placeholder="Laisser vide pour garder la valeur actuelle"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Numéro WhatsApp Production</label>
                  <Input
                    value={whatsappForm.twilio_whatsapp_from}
                    onChange={(e) => setWhatsappForm({...whatsappForm, twilio_whatsapp_from: e.target.value})}
                    placeholder="whatsapp:+447380654578"
                  />
                  <p className="text-xs text-slate-500 mt-1">Format: whatsapp:+XXXXXXXXXXX</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Admin/Support WhatsApp</label>
                  <Input
                    value={whatsappForm.admin_whatsapp}
                    onChange={(e) => setWhatsappForm({...whatsappForm, admin_whatsapp: e.target.value})}
                    placeholder="+33612345678"
                  />
                  <p className="text-xs text-slate-500 mt-1">Numéro pour recevoir les notifications admin et support client (bulle de contact)</p>
                </div>
                <Button onClick={handleSaveWhatsApp} disabled={savingWhatsApp} className="bg-green-600 hover:bg-green-700">
                  {savingWhatsApp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
              </div>
            ) : (
              <>
                {/* WhatsApp Mode Display */}
                <div className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Mode WhatsApp</h4>
                      <p className="text-sm text-slate-500">
                        {config?.whatsapp?.sandbox_mode 
                          ? "Mode SANDBOX - Messages envoyés via le numéro de test Twilio"
                          : "Mode PRODUCTION - Messages envoyés via votre numéro WhatsApp Business"
                        }
                      </p>
                    </div>
                    <Badge className={config?.whatsapp?.sandbox_mode ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}>
                      {config?.whatsapp?.sandbox_mode ? "SANDBOX" : "PRODUCTION"}
                    </Badge>
                  </div>
                </div>

                {/* WhatsApp Config Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Account SID</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm ${config?.whatsapp?.twilio_account_sid_set ? "text-green-600" : "text-red-600"}`}>
                          {config?.whatsapp?.twilio_account_sid_set ? config?.whatsapp?.twilio_account_sid : "❌ Non configuré"}
                        </span>
                        <SourceBadge fromDb={config?.whatsapp?.from_database?.twilio_account_sid} />
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Auth Token</span>
                      <span className={`font-mono text-sm ${config?.whatsapp?.twilio_auth_token_set ? "text-green-600" : "text-red-600"}`}>
                        {config?.whatsapp?.twilio_auth_token_set ? "✅ Configuré" : "❌ Non configuré"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Numéro actif</span>
                      <span className="font-mono text-sm font-bold text-orange-600">{config?.whatsapp?.twilio_whatsapp_from}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Admin WhatsApp</span>
                      <span className="font-mono text-sm">{config?.whatsapp?.admin_whatsapp}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Test WhatsApp */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">🧪 Envoyer un WhatsApp de test</h4>
              <div className="flex gap-3">
                <Input
                  type="tel"
                  placeholder="Numéro (ex: +33612345678)"
                  value={testWhatsAppNumber}
                  onChange={(e) => setTestWhatsAppNumber(e.target.value)}
                  className="max-w-xs"
                />
                <Button
                  onClick={handleTestWhatsApp}
                  disabled={testingWhatsApp}
                  className="bg-green-500 hover:bg-green-600"
                >
                  {testingWhatsApp ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer Test
                </Button>
              </div>
              {config?.whatsapp?.sandbox_mode && (
                <p className="text-sm text-amber-600 mt-2">
                  ⚠️ En mode Sandbox, le destinataire doit avoir préalablement envoyé "join [code]" au numéro Twilio.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Diagnostic Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-purple-500" />
              <CardTitle>Diagnostic Détaillé</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-60 overflow-y-auto">
              {JSON.stringify(diagnostic, null, 2)}
            </pre>
          </CardContent>
        </Card>

        {/* Logs Viewer */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                <div>
                  <CardTitle>Logs Serveur</CardTitle>
                  <CardDescription>Dernières entrées des logs backend (3 dernières heures)</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={logsFilter}
                  onChange={(e) => fetchLogs(e.target.value)}
                  className="text-sm border rounded-md px-2 py-1"
                >
                  <option value="all">Tous les logs</option>
                  <option value="errors">Erreurs uniquement</option>
                  <option value="email">Email/Mailgun</option>
                  <option value="whatsapp">WhatsApp/Twilio</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(logsFilter)}
                  disabled={logsLoading}
                >
                  {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!logs ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Cliquez sur le bouton pour charger les logs</p>
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => fetchLogs("all")}
                >
                  Charger les logs
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                {logs.total_matching !== undefined && (
                  <div className="flex items-center gap-4 p-3 bg-slate-100 rounded-lg">
                    <Badge variant="outline">{logs.count} lignes affichées</Badge>
                    <Badge variant="outline">Total: {logs.total_matching}</Badge>
                    <Badge variant="outline" className="capitalize">Filtre: {logs.filter}</Badge>
                  </div>
                )}
                
                {/* Logs content */}
                <div className="bg-slate-900 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto p-4">
                    {logs.logs && logs.logs.length > 0 ? (
                      <div className="space-y-1 font-mono text-xs">
                        {logs.logs.map((line, i) => {
                          const isError = line.toLowerCase().includes('error') || line.toLowerCase().includes('critical') || line.toLowerCase().includes('failed');
                          const isWarning = line.toLowerCase().includes('warning') || line.toLowerCase().includes('warn');
                          const isSuccess = line.toLowerCase().includes('success') || line.toLowerCase().includes('200');
                          
                          return (
                            <div 
                              key={i} 
                              className={`py-0.5 ${
                                isError ? 'text-red-400 bg-red-900/20' : 
                                isWarning ? 'text-amber-400' : 
                                isSuccess ? 'text-green-400' : 
                                'text-slate-300'
                              }`}
                            >
                              {line}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">Aucun log trouvé pour ce filtre</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
